import { axiosInstance } from "@/utils/lib/axios";
import { RFSData } from "./rfs";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

interface NNEvidenceData {
  id: string;
  updatedDate?: string;
  documentNnEvidenceName: string;
  documentNnEvidencePath: string;
  valid: boolean;
  revise: boolean;
  statusValidation: string;
  rfs: RFSData;
}

interface NNEvidenceInvalid {
  id: string;
  result: string;
  nnEvidence: NNEvidenceData;
}

/* ================ *
 * Read NN Evidence *
 * ================ */

interface NNEvidencesQueryKey {
  rfsId: string;
}

async function getNNEvidences(
  queryFunctionContext: QueryFunctionContext,
): Promise<NNEvidenceData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, nnEvidenceQueryKey] = queryKey;
  const { rfsId } = nnEvidenceQueryKey as NNEvidencesQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/nn-evidence" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}

function useNNEvidences(nnEvidenceQueryKey: NNEvidencesQueryKey) {
  const { rfsId } = nnEvidenceQueryKey;
  return useQuery({
    queryKey: ["nnEvidences", { rfsId }],
    queryFn: getNNEvidences,
  });
}

/* ================ *
 * Read NN Evidence Invalid *
 * ================ */

interface NNEvidenceInvalidsQueryKey {
  rfsId?: string;
}

async function getNNEvidenceInvalids(
  queryFunctionContext: QueryFunctionContext,
): Promise<NNEvidenceInvalid[]> {
  const { queryKey } = queryFunctionContext;
  const [_, nnEvidenceInvalidQueryKey] = queryKey;
  const { rfsId } = nnEvidenceInvalidQueryKey as NNEvidenceInvalidsQueryKey;

  const repsonse = await axiosInstance.get(
    "/api/crud/nn-evidence-invalid" + `?nnEvidence.rfs.id=${rfsId ?? ""}`,
  );
  return repsonse.data.data.content;
}

function useNNEvidenceInvalids(
  nnEvidenceInvalidQueryKey: NNEvidenceInvalidsQueryKey,
) {
  const { rfsId } = nnEvidenceInvalidQueryKey;
  return useQuery({
    queryKey: ["nnEvidenceInvalids", { rfsId }],
    queryFn: getNNEvidenceInvalids,
  });
}

/* ==================== *
 * Validate NN Evidence *
 * ==================== */

async function cancelNNEvidence(id: string) {
  const response = await axiosInstance.post(
    `/api/crud/nn-evidence/${id}/cancel`,
    {},
  );
  return response.data;
}

function useNNEvidenceCancel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => cancelNNEvidence(id),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["nnEvidences", { rfsId: id }],
      });
    },
  });
}

export type { NNEvidenceData, NNEvidenceInvalid };
export { useNNEvidences, useNNEvidenceInvalids, useNNEvidenceCancel };
